/* Chef Akila – homepage behaviour: carousels (curry boxes, reviews), video, "Add box to basket". */
(() => {
  if (window.CAHome) return;

  const mobile = () => window.matchMedia('(max-width: 989px)').matches;

  /* ---------- Carousels ---------- */
  const initCarousel = (root) => {
    if (!root || root.dataset.caReady) return;
    root.dataset.caReady = '1';
    const track = root.querySelector('[data-track]');
    const nav = root.querySelector('[data-nav]');
    const dotsWrap = root.querySelector('[data-dots]');
    const prev = root.querySelector('[data-prev]');
    const next = root.querySelector('[data-next]');
    if (!track) return;
    // Endless mode (data-loop): a copy of the cards either side, and the track quietly jumps back to the middle copy once it settles
    const originals = Array.from(track.children).filter((el) => el.offsetParent !== null);
    const n = originals.length;
    const loop = root.hasAttribute('data-loop') && n > 1;
    if (loop) {
      const copy = (el) => {
        const c = el.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('inert', '');
        c.dataset.clone = '';
        c.removeAttribute('data-shopify-editor-block');
        return c;
      };
      track.prepend(...originals.map(copy));
      track.append(...originals.map(copy));
    }
    const items = () => Array.from(track.children).filter((el) => el.offsetParent !== null);
    let stops = [];

    const centred = () => getComputedStyle(items()[0] || track).scrollSnapAlign.includes('center');
    const max = () => track.scrollWidth - track.clientWidth;
    const padStart = () => parseFloat(getComputedStyle(track).paddingLeft) || 0;

    // Scroll positions where an item lines up, de-duplicated and clamped to the scrollable range
    const computeStops = () => {
      const list = items();
      const m = max();
      const c = centred();
      const raw = list.map((el) => {
        const x = c ? el.offsetLeft + el.offsetWidth / 2 - track.clientWidth / 2 : el.offsetLeft - padStart();
        return Math.max(0, Math.min(m, Math.round(x)));
      });
      if (loop) return raw; // the copies keep the real cards well away from both ends
      const out = [];
      raw.forEach((x) => { if (!out.length || Math.abs(x - out[out.length - 1]) > 4) out.push(x); });
      return out;
    };
    const real = (i) => (loop ? ((i % n) + n) % n : i);

    const current = () => {
      let best = 0;
      stops.forEach((x, i) => { if (Math.abs(x - track.scrollLeft) < Math.abs(stops[best] - track.scrollLeft)) best = i; });
      return best;
    };

    const go = (i, smooth = true) => {
      const idx = Math.max(0, Math.min(stops.length - 1, i));
      track.scrollTo({ left: stops[idx], behavior: smooth ? 'smooth' : 'auto' });
    };

    const sync = () => {
      const i = real(current());
      dotsWrap?.querySelectorAll('button').forEach((b, k) => b.setAttribute('aria-current', String(k === i)));
      if (loop) return;
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft >= max() - 4;
    };

    // Endless mode: once scrolling stops on a copy, jump (invisibly) to the same card in the middle set
    let settleTimer;
    let dragging = false;
    const recentre = () => {
      if (!loop || dragging) return;
      const i = current();
      if (i >= n && i < 2 * n) return;
      track.scrollTo({ left: stops[real(i) + n], behavior: 'instant' });
    };

    const build = () => {
      const keep = loop && stops.length ? real(current()) : null;
      stops = computeStops();
      if (nav) nav.hidden = (loop ? n : stops.length) < 2;
      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        const count = loop ? n : stops.length;
        for (let i = 0; i < count; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'ca-hp-carousel__dot';
          b.setAttribute('aria-label', `Go to slide ${i + 1} of ${count}`);
          b.addEventListener('click', () => go(loop ? i + n : i));
          dotsWrap.appendChild(b);
        }
      }
      if (keep !== null) track.scrollTo({ left: stops[keep + n], behavior: 'instant' });
      sync();
    };

    prev?.addEventListener('click', () => go(current() - 1));
    next?.addEventListener('click', () => go(current() + 1));
    track.addEventListener('scroll', () => {
      requestAnimationFrame(sync);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(recentre, 160);
    }, { passive: true });

    // Mouse drag (touch and trackpads already scroll natively)
    if (loop) {
      let startX = 0;
      let startLeft = 0;
      let moved = 0;
      let from = 0;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        dragging = true;
        moved = 0;
        startX = e.clientX;
        startLeft = track.scrollLeft;
        from = current();
        track.classList.add('is-dragging');
      });
      window.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        moved = e.clientX - startX;
        track.scrollLeft = startLeft - moved;
      });
      window.addEventListener('pointerup', () => {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
        const target = Math.abs(moved) > 40 ? from + (moved < 0 ? 1 : -1) * Math.max(1, Math.round(Math.abs(moved) / ((stops[1] - stops[0]) || 1))) : from;
        go(target);
      });
      // A drag shouldn't count as a click on whatever it ended over
      track.addEventListener('click', (e) => { if (Math.abs(moved) > 5) { e.preventDefault(); e.stopPropagation(); moved = 0; } }, true);
      track.addEventListener('dragstart', (e) => e.preventDefault());
    }
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(current() + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(current() - 1); }
    });
    let w = window.innerWidth;
    window.addEventListener('resize', () => { if (window.innerWidth !== w) { w = window.innerWidth; build(); } });

    build();
    // Optional starting slide (so the previous card peeks in from the left, as in the design)
    const start = (Number(mobile() ? root.dataset.startMobile ?? root.dataset.start : root.dataset.start) || 0) + (loop ? n : 0);
    if (start > 0 && items().length > start + 1) {
      const el = items()[start];
      const x = centred() ? el.offsetLeft + el.offsetWidth / 2 - track.clientWidth / 2 : el.offsetLeft - padStart();
      track.scrollLeft = Math.max(0, Math.min(max(), x));
      sync();
    }
    // Images and fonts can change widths after first paint
    window.addEventListener('load', build, { once: true });
  };

  /* ---------- Video (YouTube / Vimeo / uploaded) ---------- */
  const initVideo = (btn) => {
    if (!btn || btn.dataset.caReady) return;
    btn.dataset.caReady = '1';
    btn.addEventListener('click', () => {
      if (btn.classList.contains('is-playing')) return;
      let player = null;
      if (btn.dataset.youtube) {
        player = document.createElement('iframe');
        player.src = `https://www.youtube-nocookie.com/embed/${btn.dataset.youtube}?autoplay=1&rel=0&playsinline=1`;
      } else if (btn.dataset.vimeo) {
        player = document.createElement('iframe');
        player.src = `https://player.vimeo.com/video/${btn.dataset.vimeo}?autoplay=1`;
      } else {
        const tpl = btn.querySelector('template');
        if (tpl) player = tpl.content.firstElementChild?.cloneNode(true);
      }
      if (!player) return;
      if (player.tagName === 'IFRAME') {
        player.title = btn.getAttribute('aria-label') || 'Video';
        player.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        player.allowFullscreen = true;
      }
      btn.appendChild(player);
      btn.classList.add('is-playing');
      btn.removeAttribute('aria-label');
      if (player.tagName === 'VIDEO') player.play?.().catch(() => {});
    });
  };

  /* ---------- Add a curry box to the basket (through the basket's single API, assets/ca-cart.js) ---------- */
  const addToBasket = async (id) => {
    if (window.CACart && typeof window.CACart.add === 'function') return window.CACart.add([{ id, quantity: 1 }], { source: 'hp-box' });
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Could not add to basket');
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
    if (cart) document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
    return data;
  };

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-ca-hp-add]');
    if (!btn || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();
    const id = Number(btn.dataset.caHpAdd);
    if (!id) return;
    btn.setAttribute('aria-busy', 'true');
    const label = btn.querySelector('[data-label]');
    const original = label?.textContent;
    try {
      await addToBasket(id);
      const added = btn.closest('[data-added-label]')?.dataset.addedLabel;
      if (label && added) { label.textContent = added; btn.classList.add('is-added'); }
      setTimeout(() => { if (label && original) label.textContent = original; btn.classList.remove('is-added'); }, 2500);
    } catch (err) {
      if (label) label.textContent = err.message;
      setTimeout(() => { if (label && original) label.textContent = original; }, 3500);
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });

  const init = (scope = document) => {
    scope.querySelectorAll('[data-ca-hp-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-ca-hp-video]').forEach(initVideo);
  };

  window.CAHome = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    window.CADelivery?.render?.(e.target);
  });
})();
